import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import type { DailyCallService } from '@services/dailycall';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { useRecordingContext } from '../../../context/RecordingContext';
import { useTelehealthCallManager } from '../context/useTelehealthCallManager';
import { clampToViewport, useResizable } from '../hooks/useResizable';
import { selectMiniPlayerRect, setChatPanelOpen, setMiniPlayerRect } from '../store';
import type { TelehealthSession } from '../types';

import { CompactPipView } from './pip/CompactPipView';
import { PipView } from './pip/PipView';
import { EndCallDialog } from './EndCallDialog';

interface TelehealthMiniPlayerProps {
  session: TelehealthSession;
  activeEncounterId: string;
  dailyCallService: DailyCallService;
  isRecording: boolean;
  isRecordingPaused?: boolean;
  onLeaveCall: () => void;
  onStopRecordingAndLeave: () => void;
}

const DEFAULT_WIDTH = 320;
const DEFAULT_HEIGHT = 240;
const MIN_WIDTH = 280;
const MIN_HEIGHT = 200;
const MAX_VIEWPORT_FRACTION = 0.8;
const WIDTH_BREAKPOINT = 400;

function getDefaultRect() {
  return {
    width: DEFAULT_WIDTH,
    height: DEFAULT_HEIGHT,
    x: window.innerWidth - DEFAULT_WIDTH - 24,
    y: window.innerHeight - DEFAULT_HEIGHT - 24,
  };
}

export function TelehealthMiniPlayer({
  session,
  activeEncounterId,
  dailyCallService,
  isRecording,
  isRecordingPaused,
  onLeaveCall,
  onStopRecordingAndLeave,
}: TelehealthMiniPlayerProps) {
  const navigate = useNavigate();
  const { telehealth } = useEventTracking();
  const dispatch = useAppDispatch();
  const storedRect = useAppSelector(selectMiniPlayerRect);

  const { audioInputDeviceService, setSelectedMicrophone } = useRecordingContext();
  const { patientLink, pip } = useTelehealthCallManager();

  const [showEndCallDialog, setShowEndCallDialog] = useState(false);

  // Compute initial rect + max dimensions on mount
  const initialRect = useMemo(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    return storedRect ? clampToViewport(storedRect, vw, vh) : getDefaultRect();
    // Only compute once on mount — storedRect changes are handled by the hook's sync
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [maxWidth, setMaxWidth] = useState(() => Math.round(window.innerWidth * MAX_VIEWPORT_FRACTION));
  const [maxHeight, setMaxHeight] = useState(() => Math.round(window.innerHeight * MAX_VIEWPORT_FRACTION));

  const handleRectChange = useCallback(
    (rect: { width: number; height: number; x: number; y: number }) => {
      dispatch(setMiniPlayerRect(rect));
    },
    [dispatch],
  );

  const {
    width,
    height,
    x,
    y,
    isResizing,
    isDragging,
    resizeHandles,
    dragHandleProps,
    setRect: setResizableRect,
  } = useResizable({
    initialWidth: initialRect.width,
    initialHeight: initialRect.height,
    initialX: initialRect.x,
    initialY: initialRect.y,
    minWidth: MIN_WIDTH,
    minHeight: MIN_HEIGHT,
    maxWidth,
    maxHeight,
    onResizeEnd: handleRectChange,
    onMoveEnd: handleRectChange,
  });

  // Re-clamp mini player position and size when the browser window is resized
  useEffect(() => {
    let rafId: number;
    const handleViewportResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const newMaxW = Math.round(vw * MAX_VIEWPORT_FRACTION);
        const newMaxH = Math.round(vh * MAX_VIEWPORT_FRACTION);
        setMaxWidth(newMaxW);
        setMaxHeight(newMaxH);

        setResizableRect(prev => {
          const clampedW = Math.max(MIN_WIDTH, Math.min(prev.width, newMaxW));
          const clampedH = Math.max(MIN_HEIGHT, Math.min(prev.height, newMaxH));
          const clamped = clampToViewport(
            { width: clampedW, height: clampedH, x: prev.x, y: prev.y },
            vw,
            vh,
          );
          if (
            clamped.x !== prev.x ||
            clamped.y !== prev.y ||
            clampedW !== prev.width ||
            clampedH !== prev.height
          ) {
            dispatch(setMiniPlayerRect(clamped));
            return clamped;
          }
          return prev;
        });
      });
    };

    window.addEventListener('resize', handleViewportResize);
    return () => {
      window.removeEventListener('resize', handleViewportResize);
      cancelAnimationFrame(rafId);
    };
  }, [dispatch, setResizableRect]);

  const isCompact = width < WIDTH_BREAKPOINT;

  const handleReturnToEncounter = useCallback(() => {
    navigate(`/encounters/${activeEncounterId}`);
  }, [activeEncounterId, navigate]);

  const handleOpenChat = useCallback(() => {
    dispatch(setChatPanelOpen(true));
    navigate(`/encounters/${activeEncounterId}`);
  }, [activeEncounterId, dispatch, navigate]);

  const handleLeaveClick = useCallback(() => {
    if (isRecording) {
      setShowEndCallDialog(true);
      return;
    }
    telehealth.callLeft({
      appointment_id: session.appointmentId,
      encounter_id: session.encounterId ?? '',
    });
    onLeaveCall();
  }, [isRecording, onLeaveCall, session, telehealth]);

  const handleLeaveOnly = useCallback(() => {
    setShowEndCallDialog(false);
    telehealth.callLeft({
      appointment_id: session.appointmentId,
      encounter_id: session.encounterId ?? '',
    });
    onLeaveCall();
  }, [onLeaveCall, session, telehealth]);

  const handleStopRecordingAndLeave = useCallback(() => {
    setShowEndCallDialog(false);
    telehealth.callLeft({
      appointment_id: session.appointmentId,
      encounter_id: session.encounterId ?? '',
    });
    onStopRecordingAndLeave();
  }, [onStopRecordingAndLeave, session, telehealth]);

  const isInteracting = isResizing || isDragging;

  return (
    <>
      {/* Outer container — overflow visible for resize handles that extend outside */}
      <Box
        sx={{
          position: 'fixed',
          left: x,
          top: y,
          width,
          height,
          zIndex: 1299,
          transition: isInteracting
            ? 'none'
            : 'width 200ms ease, height 200ms ease, left 200ms ease, top 200ms ease',
        }}
      >
        {/* Resize handles — absolutely positioned, extend outside the box */}
        {resizeHandles}

        {/* Inner content wrapper — clips content with border radius */}
        <Box
          {...dragHandleProps}
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            cursor: dragHandleProps.style.cursor,
            position: 'relative',
          }}
        >
          {isCompact ? (
            <CompactPipView
              dailyCallService={dailyCallService}
              isRecording={isRecording}
              isRecordingPaused={isRecordingPaused}
              isOwner={session.isOwner}
              onLeave={handleLeaveClick}
              onClose={handleReturnToEncounter}
              onEnterPip={pip.toggle}
              isPipSupported={pip.isSupported}
              onOpenChat={handleOpenChat}
            />
          ) : (
            <PipView
              dailyCallService={dailyCallService}
              audioInputDeviceService={audioInputDeviceService}
              setSelectedMicrophone={setSelectedMicrophone}
              isRecording={isRecording}
              isRecordingPaused={isRecordingPaused}
              session={session}
              onCopyLink={patientLink.handleCopyLink}
              isCopyingLink={patientLink.isFetchingLink}
              linkCopied={patientLink.linkCopied}
              onLeave={handleLeaveClick}
              onClose={handleReturnToEncounter}
              onEnterPip={pip.toggle}
              isPipSupported={pip.isSupported}
            />
          )}
        </Box>
      </Box>
      <EndCallDialog
        open={showEndCallDialog}
        isRecording={isRecording}
        onLeaveOnly={handleLeaveOnly}
        onStopRecordingAndLeave={handleStopRecordingAndLeave}
        onCancel={() => setShowEndCallDialog(false)}
      />
    </>
  );
}
